/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

import Framework, { BaseService } from '@greenville/framework';
import * as constants from '../../constants/ServiceConstants';
import * as commonConstants from '../../../../common/constants';
import utils from '../../../../common/utils';

/**
 * CopyGraphService - Handles copying a graph (same or cross-tenant)
 * API: POST /graphs/{graphId}/copy
 *
 * Payload:
 * - version: Source graph version to copy
 * - tenantId: Target tenant ID where graph will be copied
 * - graphName: Name for the copied graph
 */
export default class CopyGraphService extends BaseService {
  constructor() {
    super(constants.COPY_GRAPH, 'copyGraphStatus');
  }

  getErrorMessage(data, fallback) {
    return data?.error || data?.message || fallback;
  }

  async handleEvent(eventName, event) {
    const { payload } = event;
    const { graphId, version, tenantId, graphName, sourceTenantId } = payload;
    this.eventPayload = payload;

    // Build payload as per new contract (graphId in path, not in body)
    const requestData = {
      version,
      tenantId,
      graphName
    };

    // Set x-berlin-tenant-id header to SOURCE tenant (the tenant that owns the graph being copied)
    // while `tenantId` in body is the TARGET tenant.
    const headers = { [commonConstants.BERLIN_TENANT_ID_HEADER]: sourceTenantId || tenantId };

    const { isStackB } = utils.getStackBParams();

    if (isStackB) {
      const enhancedHeaders = await utils.resolveStackBHeaders(headers);
      return this.getClient('aiadminstackb').post(`/graphs/${graphId}/copy`, requestData, {
        headers: enhancedHeaders
      });
    }
    return this.getClient('aiadmin').post(`/graphs/${graphId}/copy`, requestData, { headers });
  }

  handleResponse(response) {
    const { data } = response;
    if (data && data.success) {
      const { targetTenantName } = this.eventPayload;
      const validationWarnings = Array.isArray(data?.validation?.warning) ? data.validation.warning : [];

      const notification =
        validationWarnings.length > 0
          ? {
              open: true,
              type: 'warning',
              message: [data.data, ...validationWarnings].join(' ')
            }
          : {
              open: true,
              type: 'success',
              langMessage: targetTenantName
                ? ['copyGraph.GRAPH_COPIED_SUCCESSFULLY', 'copyGraph.TO', targetTenantName]
                : 'copyGraph.GRAPH_COPIED_SUCCESSFULLY'
            };

      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      const graphModel = Framework.getStoreRegistry().getStore('graphModel');
      graphModel.clearError();
      Framework.getEventManager().publish(constants.COPY_GRAPH_SUCCESS, { data });
    } else {
      const msg = this.getErrorMessage(data, 'Unknown error');
      const notification = {
        open: true,
        type: 'error',
        message: `Failed to copy graph: ${msg}`
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
      Framework.getEventManager().publish(constants.COPY_GRAPH_ERROR);
    }
  }

  handleError(error) {
    const { response } = error;
    if (response) {
      const { status, data } = response;
      if (status === 401) {
        Framework.getStoreRegistry()
          .getStore('notificationState')
          .set({ open: true, message: 'Session expired. Please login again.', type: 'error' });
      }
      const msg = this.getErrorMessage(data, 'Failed to copy graph. Please try again.');
      const notification = {
        open: true,
        message: msg,
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError(msg);
    } else if (error.message === 'timeout of 60000ms exceeded') {
      const notification = {
        open: true,
        message: 'Request timeout. Please try again.',
        type: 'error'
      };
      Framework.getStoreRegistry().getStore('notificationState').set(notification);
      Framework.getStoreRegistry().getStore('graphModel').setError('Request timeout. Please try again.');
    }
    Framework.getEventManager().publish(constants.COPY_GRAPH_ERROR);
  }
}
